import { useMemo, useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  Call,
  CoachJoinMode,
  DialSessionTarget,
} from "@sales-ops-amplify/shared";
import {
  completeCall,
  deleteCallRecording,
  getCallAnalytics,
  joinActiveCall,
  listActiveCalls,
  listCalls,
  startDialSession,
} from "@/api/calls";
import { getCallConfig } from "@/api/call-config";
import { CallActionsMenu } from "@/components/calls/call-actions-menu";
import { CallInsightsPanel } from "@/components/calls/call-insights-panel";
import { CallScorecardEditor } from "@/components/calls/call-scorecard";
import { CallsAnalyticsStrip } from "@/components/calls/calls-analytics";
import { useSoftphone } from "@/components/calls/softphone-context";
import { cn } from "@/cn";
import { useDebouncedValue } from "@/hooks/usePagination";

function formatWhen(iso?: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatDuration(sec?: number): string {
  if (sec == null) return "—";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

const TABS = [
  { to: "/calls", label: "All calls", end: true },
  { to: "/calls/upcoming", label: "Upcoming" },
  { to: "/calls/new", label: "New call" },
];

export function CallsLayout() {
  return (
    <div className="space-y-6 pb-28">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Calls</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Dial, log outcomes, review recordings, and coach active calls.
          </p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/calls/new"
            className="rounded-md bg-brand px-3 py-2 text-sm text-white"
          >
            New call
          </Link>
        </div>
      </div>
      <nav className="flex gap-1 border-b border-border">
        {TABS.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            className={({ isActive }) =>
              cn(
                "px-3 py-2 text-sm",
                isActive
                  ? "border-b-2 border-brand font-medium text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )
            }
          >
            {t.label}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </div>
  );
}

export function CallsAllPage() {
  const qc = useQueryClient();
  const sp = useSoftphone();
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q.trim(), 300);
  const [purposeId, setPurposeId] = useState("");
  const [dispositionId, setDispositionId] = useState("");
  const [direction, setDirection] = useState<"" | "inbound" | "outbound">("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [minDurationSec, setMinDuration] = useState("");
  const [maxDurationSec, setMaxDuration] = useState("");
  const [showActive, setShowActive] = useState(false);
  const [selected, setSelected] = useState<Call | null>(null);
  const [joinMode, setJoinMode] = useState<CoachJoinMode>("listen");
  const [powerTargets, setPowerTargets] = useState<DialSessionTarget[]>([]);

  const configQuery = useQuery({
    queryKey: ["call-config"],
    queryFn: getCallConfig,
  });

  const listQuery = useQuery({
    queryKey: [
      "calls",
      "list",
      debouncedQ,
      purposeId,
      dispositionId,
      direction,
      dateFrom,
      dateTo,
      minDurationSec,
      maxDurationSec,
      showActive,
    ],
    queryFn: () =>
      showActive
        ? listActiveCalls()
        : listCalls({
            q: debouncedQ || undefined,
            purposeId: purposeId || undefined,
            dispositionId: dispositionId || undefined,
            direction: direction || undefined,
            dateFrom: dateFrom || undefined,
            dateTo: dateTo || undefined,
            minDurationSec: minDurationSec
              ? Number(minDurationSec)
              : undefined,
            maxDurationSec: maxDurationSec
              ? Number(maxDurationSec)
              : undefined,
            limit: 200,
          }),
  });

  const analyticsQuery = useQuery({
    queryKey: [
      "calls",
      "analytics",
      purposeId,
      dispositionId,
      direction,
      dateFrom,
      dateTo,
    ],
    queryFn: () =>
      getCallAnalytics({
        purposeId: purposeId || undefined,
        dispositionId: dispositionId || undefined,
        direction: direction || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      }),
  });

  const purposes = configQuery.data?.config.purposes ?? [];
  const dispositions = configQuery.data?.config.dispositions ?? [];
  const purposeName = useMemo(() => {
    const m = new Map(purposes.map((p) => [p.id, p.name]));
    return (id?: string) => (id ? m.get(id) ?? id : "—");
  }, [purposes]);
  const dispName = useMemo(() => {
    const m = new Map(dispositions.map((d) => [d.id, d.name]));
    return (id?: string) => (id ? m.get(id) ?? id : "—");
  }, [dispositions]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["calls"] });

  const completeMutation = useMutation({
    mutationFn: (args: {
      callId: string;
      purposeId?: string;
      dispositionId?: string;
      notes?: string;
    }) =>
      completeCall(args.callId, {
        purposeId: args.purposeId,
        dispositionId: args.dispositionId,
        notes: args.notes,
      }),
    onSuccess: (res) => {
      setSelected(res.call);
      invalidate();
    },
  });

  const joinMutation = useMutation({
    mutationFn: ({ callId, mode }: { callId: string; mode: CoachJoinMode }) =>
      joinActiveCall(callId, mode),
    onSuccess: (res, vars) => {
      invalidate();
      void sp.joinConference(res.conferenceName, vars.mode, res.call);
    },
  });

  const deleteRecMutation = useMutation({
    mutationFn: (callId: string) => deleteCallRecording(callId),
    onSuccess: (res) => {
      setSelected(res.call);
      invalidate();
    },
  });

  const powerMutation = useMutation({
    mutationFn: (type: "power" | "parallel") =>
      startDialSession({
        type,
        targets: powerTargets,
        leadIds: powerTargets.map((t) => t.leadId),
        concurrency: type === "parallel" ? 3 : 1,
      }),
    onSuccess: (res) => {
      void sp.startSessionDialing(res.session);
    },
  });

  const calls = listQuery.data?.calls ?? [];
  const scorecardCriteria =
    configQuery.data?.config.scorecardCriteria ?? [];

  return (
    <div className="space-y-4">
      <CallsAnalyticsStrip
        analytics={analyticsQuery.data?.analytics}
        loading={analyticsQuery.isLoading}
        dispositionName={dispName}
      />

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={showActive}
            onChange={(e) => setShowActive(e.target.checked)}
          />
          Show active calls
        </label>
        {powerTargets.length > 0 && (
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded-md border border-border px-2 py-1 text-xs"
              onClick={() => powerMutation.mutate("power")}
            >
              Power dial ({powerTargets.length})
            </button>
            <button
              type="button"
              className="rounded-md border border-border px-2 py-1 text-xs"
              onClick={() => powerMutation.mutate("parallel")}
            >
              Parallel dial
            </button>
          </div>
        )}
      </div>

      <div className="grid gap-2 rounded-lg border border-border p-3 sm:grid-cols-2 lg:grid-cols-4">
        <input
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          placeholder="Search contact / number"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          value={purposeId}
          onChange={(e) => setPurposeId(e.target.value)}
        >
          <option value="">Purpose</option>
          {purposes.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <select
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          value={dispositionId}
          onChange={(e) => setDispositionId(e.target.value)}
        >
          <option value="">Disposition</option>
          {dispositions.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
        <select
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          value={direction}
          onChange={(e) =>
            setDirection(e.target.value as "" | "inbound" | "outbound")
          }
        >
          <option value="">Direction</option>
          <option value="outbound">Outbound</option>
          <option value="inbound">Inbound</option>
        </select>
        <input
          type="date"
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
        />
        <input
          type="date"
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
        />
        <input
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          placeholder="Min duration (sec)"
          value={minDurationSec}
          onChange={(e) => setMinDuration(e.target.value)}
        />
        <input
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          placeholder="Max duration (sec)"
          value={maxDurationSec}
          onChange={(e) => setMaxDuration(e.target.value)}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_420px]">
        <div className="min-w-0 overflow-x-auto overscroll-x-contain rounded-lg border border-border [-webkit-overflow-scrolling:touch]">
          <table className="w-full min-w-[880px] text-left text-sm">
            <thead className="border-b border-border bg-surface/50 text-xs text-muted-foreground">
              <tr>
                <th className="w-8 px-2 py-2" />
                <th className="px-3 py-2">Contact</th>
                <th className="px-3 py-2">Direction</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Purpose</th>
                <th className="px-3 py-2">Disposition</th>
                <th className="px-3 py-2">Duration</th>
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {listQuery.isLoading && (
                <tr>
                  <td colSpan={9} className="px-3 py-6 text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              )}
              {!listQuery.isLoading && calls.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-6 text-muted-foreground">
                    No calls yet.{" "}
                    <Link to="/calls/new" className="text-brand underline">
                      Start a new call
                    </Link>
                  </td>
                </tr>
              )}
              {calls.map((c) => (
                <tr
                  key={c.callId}
                  className={cn(
                    "border-b border-border/60 hover:bg-surface-hover/50",
                    selected?.callId === c.callId && "bg-surface-hover/80",
                  )}
                >
                  <td className="px-2 py-2">
                    {c.leadId && (
                      <input
                        type="checkbox"
                        checked={powerTargets.some(
                          (t) => t.leadId === c.leadId,
                        )}
                        onChange={(e) => {
                          setPowerTargets((prev) =>
                            e.target.checked
                              ? [
                                  ...prev,
                                  {
                                    leadId: c.leadId!,
                                    to: c.toNumber,
                                    leadName: c.leadName,
                                    opportunityId: c.opportunityId,
                                    accountId: c.accountId,
                                  },
                                ]
                              : prev.filter(
                                  (t) => t.leadId !== c.leadId,
                                ),
                          );
                        }}
                      />
                    )}
                  </td>
                  <td
                    className="cursor-pointer px-3 py-2"
                    onClick={() => setSelected(c)}
                  >
                    <div className="font-medium">
                      {c.leadName || c.toNumber || c.fromNumber || "—"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {c.toNumber || c.fromNumber}
                    </div>
                  </td>
                  <td className="px-3 py-2 capitalize">{c.direction}</td>
                  <td className="px-3 py-2">{c.status}</td>
                  <td className="px-3 py-2">{purposeName(c.purposeId)}</td>
                  <td className="px-3 py-2">{dispName(c.dispositionId)}</td>
                  <td className="px-3 py-2">{formatDuration(c.durationSec)}</td>
                  <td className="px-3 py-2">
                    {formatWhen(c.startedAt ?? c.createdAt)}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap items-center justify-end gap-1">
                      {(c.isActive ||
                        c.status === "in_progress" ||
                        c.status === "ringing") && (
                        <>
                          <select
                            className="rounded border border-border text-xs"
                            value={joinMode}
                            onChange={(e) =>
                              setJoinMode(e.target.value as CoachJoinMode)
                            }
                          >
                            <option value="listen">Listen</option>
                            <option value="coach">Coach</option>
                            <option value="barge">Barge</option>
                          </select>
                          <button
                            type="button"
                            className="text-xs text-brand underline"
                            onClick={() =>
                              joinMutation.mutate({
                                callId: c.callId,
                                mode: joinMode,
                              })
                            }
                          >
                            Join
                          </button>
                        </>
                      )}
                      {c.status === "pending" && c.toNumber && (
                        <button
                          type="button"
                          className="text-xs text-brand underline"
                          onClick={() =>
                            void sp.dial({
                              to: c.toNumber!,
                              leadId: c.leadId,
                              leadName: c.leadName,
                              opportunityId: c.opportunityId,
                              accountId: c.accountId,
                              sequenceId: c.sequenceId,
                              enrollmentId: c.enrollmentId,
                            })
                          }
                        >
                          Call
                        </button>
                      )}
                      <CallActionsMenu
                        call={c}
                        compact
                        onCallUpdated={(updated) => {
                          if (selected?.callId === updated.callId) {
                            setSelected(updated);
                          }
                          invalidate();
                        }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <aside className="max-h-[calc(100vh-12rem)] space-y-3 overflow-y-auto rounded-lg border border-border p-4">
          {!selected && (
            <p className="text-sm text-muted-foreground">
              Select a call to view AI insights, recording, and details.
            </p>
          )}
          {selected && (
            <div className="space-y-3 text-sm">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h2 className="font-medium">
                    {selected.leadName || selected.toNumber || "Call"}
                    {selected.interested ? (
                      <span className="ml-2 text-xs font-normal text-brand">
                        Interested
                      </span>
                    ) : null}
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    {selected.direction} · {selected.status} ·{" "}
                    {formatWhen(selected.startedAt ?? selected.createdAt)}
                  </p>
                </div>
                <CallActionsMenu
                  call={selected}
                  onCallUpdated={(c) => {
                    setSelected(c);
                    invalidate();
                  }}
                />
              </div>
              {/* Insights first — Apollo Conversations-style surface */}
              <CallInsightsPanel
                call={selected}
                onCallUpdated={(c) => {
                  setSelected(c);
                  invalidate();
                }}
              />

              {selected.recordingUrl && (
                <div className="space-y-1">
                  <audio
                    controls
                    className="w-full"
                    src={selected.recordingUrl}
                  />
                  <button
                    type="button"
                    className="text-xs text-red-600 underline"
                    onClick={() =>
                      deleteRecMutation.mutate(selected.callId)
                    }
                  >
                    Delete recording
                  </button>
                </div>
              )}

              <label className="block text-xs">
                Purpose
                <select
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                  value={selected.purposeId ?? ""}
                  onChange={(e) =>
                    setSelected({ ...selected, purposeId: e.target.value })
                  }
                >
                  <option value="">—</option>
                  {purposes.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-xs">
                Disposition
                <select
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                  value={selected.dispositionId ?? ""}
                  onChange={(e) =>
                    setSelected({ ...selected, dispositionId: e.target.value })
                  }
                >
                  <option value="">—</option>
                  {dispositions.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-xs">
                Notes
                <textarea
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                  rows={3}
                  value={selected.notes ?? ""}
                  onChange={(e) =>
                    setSelected({ ...selected, notes: e.target.value })
                  }
                />
              </label>
              <CallScorecardEditor
                call={selected}
                criteria={scorecardCriteria}
                onCallUpdated={(c) => {
                  setSelected(c);
                  invalidate();
                }}
              />
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="rounded-md bg-brand px-3 py-1.5 text-white"
                  onClick={() =>
                    completeMutation.mutate({
                      callId: selected.callId,
                      purposeId: selected.purposeId,
                      dispositionId: selected.dispositionId,
                      notes: selected.notes,
                    })
                  }
                >
                  Complete call
                </button>
                {selected.toNumber && (
                  <button
                    type="button"
                    className="rounded-md border border-border px-3 py-1.5"
                    onClick={() =>
                      void sp.dial({
                        to: selected.toNumber!,
                        leadId: selected.leadId,
                        leadName: selected.leadName,
                        useLocalPresence: true,
                      })
                    }
                  >
                    Call again
                  </button>
                )}
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

export function CallsUpcomingPage() {
  const sp = useSoftphone();
  const listQuery = useQuery({
    queryKey: ["calls", "upcoming"],
    queryFn: () => listCalls({ status: "pending", limit: 200 }),
  });
  const calls = listQuery.data?.calls ?? [];

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Call tasks from sequences and manually queued contacts.
      </p>
      <div className="min-w-0 overflow-x-auto overscroll-x-contain rounded-lg border border-border [-webkit-overflow-scrolling:touch]">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="border-b border-border bg-surface/50 text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Contact</th>
              <th className="px-3 py-2">Number</th>
              <th className="px-3 py-2">Sequence</th>
              <th className="px-3 py-2">Created</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {calls.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-muted-foreground">
                  No upcoming calls.
                </td>
              </tr>
            )}
            {calls.map((c) => (
              <tr key={c.callId} className="border-b border-border/60">
                <td className="px-3 py-2">{c.leadName || "—"}</td>
                <td className="px-3 py-2">{c.toNumber || "—"}</td>
                <td className="px-3 py-2">
                  {c.sequenceId ? (
                    <Link
                      to={`/sequences/${c.sequenceId}`}
                      className="text-brand underline"
                    >
                      Sequence
                    </Link>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="px-3 py-2">{formatWhen(c.createdAt)}</td>
                <td className="px-3 py-2">
                  <button
                    type="button"
                    disabled={!c.toNumber}
                    className="text-brand underline disabled:opacity-40"
                    onClick={() =>
                      c.toNumber &&
                      void sp.dial({
                        to: c.toNumber,
                        leadId: c.leadId,
                        leadName: c.leadName,
                        sequenceId: c.sequenceId,
                        enrollmentId: c.enrollmentId,
                        accountId: c.accountId,
                      })
                    }
                  >
                    Call
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function CallsNewPage() {
  const sp = useSoftphone();
  const [to, setTo] = useState("");
  const [leadName, setContactName] = useState("");
  const [useLocalPresence, setUseLocalPresence] = useState(true);

  return (
    <div className="mx-auto max-w-md space-y-4">
      <p className="text-sm text-muted-foreground">
        Enter a phone number (E.164) or search-linked contact number to dial.
      </p>
      <label className="block text-sm">
        Phone number
        <input
          className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
          placeholder="+1…"
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
      </label>
      <label className="block text-sm">
        Contact name (optional)
        <input
          className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
          value={leadName}
          onChange={(e) => setContactName(e.target.value)}
        />
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={useLocalPresence}
          onChange={(e) => setUseLocalPresence(e.target.checked)}
        />
        Use local presence caller ID when available
      </label>
      <button
        type="button"
        disabled={!to.trim()}
        className="w-full rounded-md bg-brand py-2 text-sm text-white disabled:opacity-40"
        onClick={() =>
          void sp.dial({
            to: to.trim(),
            leadName: leadName.trim() || undefined,
            useLocalPresence,
          })
        }
      >
        Call
      </button>
      {!sp.twilioConfigured && (
        <p className="text-xs text-muted-foreground">
          Twilio is not configured — this creates a call record you can complete
          with purpose/disposition. Configure env vars to enable live VoIP.
        </p>
      )}
    </div>
  );
}
